import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { FiX, FiCalendar, FiClock, FiLink, FiCheck } from "react-icons/fi";
import { parseBackendDate } from "../../utils/dateUtils";

const API_URL = process.env.REACT_APP_API_URL;

const formatSlot = (slot) => {
  const start = parseBackendDate(slot.startDate);
  const end = slot.endDate ? parseBackendDate(slot.endDate) : null;
  if (!start) return slot._id;
  const dateStr = start.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  const startTime = start.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
  const endTime = end ? ` – ${end.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}` : "";
  return `${dateStr}  ${startTime}${endTime}`;
};

const FreeSessionModal = ({ onClose, onSuccess }) => {
  const [therapists, setTherapists] = useState([]);
  const [users, setUsers] = useState([]);
  const [slots, setSlots] = useState([]);

  const [therapistId, setTherapistId] = useState("");
  const [userId, setUserId] = useState("");
  const [selectedSlotIds, setSelectedSlotIds] = useState([]);

  const [loadingTherapists, setLoadingTherapists] = useState(true);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [meetingUrl, setMeetingUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    axios
      .get(`${API_URL}/therapists`, { headers })
      .then((res) => setTherapists(res.data?.data || res.data || []))
      .catch(() => toast.error("Failed to load therapists"))
      .finally(() => setLoadingTherapists(false));

    axios
      .get(`${API_URL}/users`, { headers })
      .then((res) => setUsers(res.data?.data || res.data || []))
      .catch(() => toast.error("Failed to load users"))
      .finally(() => setLoadingUsers(false));
  }, []);

  useEffect(() => {
    if (!therapistId) {
      setSlots([]);
      setSelectedSlotIds([]);
      return;
    }
    const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
    setLoadingSlots(true);
    setSelectedSlotIds([]);
    axios
      .get(`${API_URL}/availabilities/therapist/${therapistId}`, { headers })
      .then((res) => {
        const all = res.data?.data || res.data || [];
        const now = new Date();
        const future = all.filter((s) => {
          const start = parseBackendDate(s.startDate);
          return start && start > now;
        });
        setSlots(future);
      })
      .catch(() => toast.error("Failed to load availability slots"))
      .finally(() => setLoadingSlots(false));
  }, [therapistId]);

  const toggleSlot = (id) => {
    setSelectedSlotIds((prev) =>
      prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]
    );
  };

  const handleSubmit = async () => {
    if (!therapistId || !userId || selectedSlotIds.length === 0) {
      toast.error("Please select therapist, client, and at least one slot.");
      return;
    }
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };
      const res = await axios.post(
        `${API_URL}/requests/admin/complimentary-session`,
        { therapistId, userId, availabilityIds: selectedSlotIds },
        { headers }
      );
      const url = res.data?.data?.meeting?.meetingUrl;
      toast.success("Free session created successfully.");
      onSuccess?.();
      if (url) {
        setMeetingUrl(url);
      } else {
        onClose();
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to create free session.";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(meetingUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const isLoading = loadingTherapists || loadingUsers;

  if (meetingUrl) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-900">Session Created</h2>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
              <FiX className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center justify-center w-12 h-12 bg-green-100 rounded-full mx-auto mb-4">
            <FiCheck className="w-6 h-6 text-green-600" />
          </div>
          <p className="text-center text-sm text-gray-500 mb-5">
            Free session created. Emails sent to therapist and client.
          </p>

          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">Meeting Link</label>
            <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
              <FiLink className="w-4 h-4 text-gray-400 shrink-0" />
              <span className="flex-1 text-sm text-gray-700 truncate">{meetingUrl}</span>
              <button
                onClick={handleCopy}
                className="text-xs font-medium text-blue-600 hover:text-blue-700 shrink-0"
              >
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-full px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg font-bold text-gray-900">Create Free Session</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <FiX className="w-5 h-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Therapist */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Therapist</label>
              <select
                value={therapistId}
                onChange={(e) => setTherapistId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                <option value="">Select therapist</option>
                {therapists.map((t) => (
                  <option key={t._id} value={t._id}>
                    {`${t.name || ""} ${t.surname || ""}`.trim() || t.email}
                  </option>
                ))}
              </select>
            </div>

            {/* Client */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client</label>
              <select
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300"
              >
                <option value="">Select client</option>
                {users.map((u) => (
                  <option key={u._id} value={u._id}>
                    {`${u.name || ""} ${u.surname || ""}`.trim() || u.email}
                  </option>
                ))}
              </select>
            </div>

            {/* Slots */}
            {therapistId && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Available Slots
                  {selectedSlotIds.length > 0 && (
                    <span className="ml-2 text-blue-600">({selectedSlotIds.length} selected)</span>
                  )}
                </label>

                {loadingSlots ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500" />
                  </div>
                ) : slots.length === 0 ? (
                  <p className="text-sm text-gray-400 py-2">No available future slots for this therapist.</p>
                ) : (
                  <div className="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-48 overflow-y-auto">
                    {slots.map((slot) => {
                      const selected = selectedSlotIds.includes(slot._id);
                      return (
                        <button
                          key={slot._id}
                          type="button"
                          onClick={() => toggleSlot(slot._id)}
                          className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 transition-colors ${
                            selected
                              ? "bg-blue-50 text-blue-700"
                              : "text-gray-700 hover:bg-gray-50"
                          }`}
                        >
                          <FiCalendar className="w-3.5 h-3.5 shrink-0" />
                          <FiClock className="w-3.5 h-3.5 shrink-0" />
                          {formatSlot(slot)}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !therapistId || !userId || selectedSlotIds.length === 0}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? "Creating..." : "Create Free Session"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FreeSessionModal;
